'use client';

import * as React from 'react';
import { useEffect, useState, useRef } from 'react';
import { Check, Loader2, Eye, MoreVertical, ArrowDown, ChevronLeft, ChevronRight } from 'lucide-react';
import { motion, AnimatePresence } from 'motion/react';

import { Badge } from '@workspace/ui/components/badge';
import { Button } from "@workspace/ui/components/button";
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow
} from '@workspace/ui/components/table';
import { ScrollArea } from '@workspace/ui/components/scroll-area';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@workspace/ui/components/select";

interface ProcessingFile {
    id: string;
    name: string;
    status: 'processing' | 'completed' | 'error';
    phase: 'extracting' | 'categorizing' | 'validating' | 'completed';
    progress: number;
    message: string;
    // Completed data
    accounts?: { debit: string; credit: string; name: string }[];
    amount?: string;
    currency?: string;
    tax?: string;
    invoiceNumber?: string;
    date?: string;
    convertedAmount?: string; // For non-EUR
}

const MOCK_FILES = [
    {
        name: 'Rechnung_Telekom_Mai.pdf',
        amount: '49,95',
        currency: 'EUR',
        tax: '19% USt./VSt. (Brutto)',
        accounts: [
            { debit: 'Telekommunikation', credit: 'Bank', name: 'Telekom Deutschland' }
        ],
        invoiceNumber: 'RE-2024-001',
        date: '02.05.2024'
    },
    {
        name: 'AWS_Invoice_May.pdf',
        amount: '1.250,00',
        currency: 'USD',
        convertedAmount: '1.157,41',
        tax: 'ohne USt./VSt. (Netto)', // Reverse Charge often treated as net or specific key
        accounts: [
            { debit: 'Fremdleistungen', credit: 'Kreditkarte', name: 'Amazon Web Services' }
        ],
        invoiceNumber: 'AWS-99283',
        date: '05.05.2024'
    },
    {
        name: 'Hotel_Berlin_Müller.jpg',
        amount: '128,50',
        currency: 'EUR',
        tax: '7% USt./VSt. (Brutto)',
        accounts: [
            { debit: 'Reisekosten', credit: 'Bank', name: 'Hotel Berlin Mitte' }
        ],
        invoiceNumber: 'H-29283',
        date: '12.05.2024'
    },
    {
        name: 'DB_Ticket_München.pdf',
        amount: '89,90',
        currency: 'EUR',
        tax: '19% USt./VSt. (Brutto)',
        accounts: [
            { debit: 'Reisekosten', credit: 'Kreditkarte', name: 'Deutsche Bahn' }
        ],
        invoiceNumber: 'T-2938',
        date: '15.05.2024'
    },
    {
        name: 'Apple_Store_MacBook.pdf',
        amount: '2.499,00',
        currency: 'EUR',
        tax: '19% USt./VSt. (Brutto)',
        accounts: [
            { debit: 'Büroausstattung', credit: 'Bank', name: 'Apple Store' }
        ],
        invoiceNumber: 'AP-9928',
        date: '20.05.2024'
    }
];

const PHASES = [
    { phase: 'extracting', message: 'Daten werden extrahiert...', duration: 1500 },
    { phase: 'categorizing', message: 'Konto wird ermittelt...', duration: 1200 },
    { phase: 'validating', message: 'Umsatzsteuer wird geprüft...', duration: 1000 },
    { phase: 'completed', message: 'Bereit für Export', duration: 0 }
];

export function ProcessingDemo(): React.JSX.Element {
    const [files, setFiles] = useState<ProcessingFile[]>([]);
    const fileIndexRef = useRef(0);

    // Simulate new file uploads
    useEffect(() => {
        // Initial file
        if (fileIndexRef.current === 0) {
            const newFile = MOCK_FILES[0];
            const fileId = Math.random().toString(36).substr(2, 9);
            setFiles([{
                id: fileId,
                name: newFile.name,
                status: 'processing',
                phase: 'extracting',
                progress: 10,
                message: 'Upload abgeschlossen...',
                accounts: newFile.accounts,
                amount: newFile.amount,
                currency: newFile.currency,
                convertedAmount: newFile.convertedAmount,
                tax: newFile.tax,
                invoiceNumber: newFile.invoiceNumber,
                date: newFile.date
            }]);
            fileIndexRef.current = 1;
        }

        const addInterval = setInterval(() => {
            setFiles(prev => {
                // Count currently processing files
                const processingCount = prev.filter(f => f.status === 'processing').length;

                // Limit concurrent processing
                if (processingCount < 2) {
                    const index = fileIndexRef.current;
                    const newFile = MOCK_FILES[index % MOCK_FILES.length];
                    const fileId = Math.random().toString(36).substr(2, 9);

                    fileIndexRef.current = index + 1;

                    const newEntry: ProcessingFile = {
                        id: fileId,
                        name: newFile.name,
                        status: 'processing',
                        phase: 'extracting',
                        progress: 0,
                        message: 'Upload abgeschlossen...',
                        accounts: newFile.accounts,
                        amount: newFile.amount,
                        currency: newFile.currency,
                        convertedAmount: newFile.convertedAmount,
                        tax: newFile.tax,
                        invoiceNumber: newFile.invoiceNumber,
                        date: newFile.date
                    };

                    // Prepend new file to top, limit total history size
                    return [newEntry, ...prev].slice(0, 7);
                }
                return prev;
            });
        }, 2500);

        return () => clearInterval(addInterval);
    }, []);

    // Simulate progress (Pure state update)
    useEffect(() => {
        const interval = setInterval(() => {
            setFiles((prevFiles) => {
                return prevFiles.map(file => {
                    if (file.status === 'completed') return file;

                    const newProgress = file.progress + 1.5;
                    let newPhase = file.phase;
                    let newMessage = file.message;
                    let newStatus: ProcessingFile['status'] = file.status;

                    if (newProgress < 30) {
                        newPhase = 'extracting';
                        newMessage = PHASES[0].message;
                    } else if (newProgress < 60) {
                        newPhase = 'categorizing';
                        newMessage = PHASES[1].message;
                    } else if (newProgress < 90) {
                        newPhase = 'validating';
                        newMessage = PHASES[2].message;
                    } else if (newProgress >= 100) {
                        newPhase = 'completed';
                        newStatus = 'completed';
                        newMessage = PHASES[3].message;
                    }

                    return {
                        ...file,
                        progress: Math.min(newProgress, 100),
                        phase: newPhase,
                        message: newMessage,
                        status: newStatus
                    };
                });
            });
        }, 80);

        return () => clearInterval(interval);
    }, []);

    return (
        <div className="w-full flex flex-col rounded-md border bg-background">
            {/* Fixed Header Table */}
            <div className="bg-muted/50 border-b hidden md:block">
                <Table className="table-fixed w-full">
                    <TableHeader>
                        <TableRow className="border-none hover:bg-transparent">
                            <TableHead className="w-[50px]"></TableHead>
                            <TableHead className="text-sm font-medium">
                                <Button variant="ghost" size="sm" className="h-8 px-2 -ml-2">
                                    Datum <ArrowDown className="ml-2 h-4 w-4" />
                                </Button>
                            </TableHead>
                            <TableHead className="text-sm font-medium">Konten</TableHead>
                            <TableHead className="text-right min-w-[300px] whitespace-nowrap text-sm font-medium">Betrag</TableHead>
                            <TableHead className="w-[50px]"></TableHead>
                        </TableRow>
                    </TableHeader>
                </Table>
            </div>

            {/* Scrollable Body - Desktop uses ScrollArea, Mobile uses overflow-hidden to avoid scroll trapping */}
            <div className="h-[280px] md:h-[380px]">
                {/* Mobile: No internal scroll - prevents scroll trapping */}
                <div className="h-full overflow-hidden md:hidden">
                    <Table className="table-fixed w-full">
                        <TableBody>
                            <AnimatePresence mode='popLayout'>
                                {files.slice(0, 4).map((file) => (
                                    <motion.tr
                                        layout
                                        initial={{ opacity: 0, x: -20 }}
                                        animate={{ opacity: 1, x: 0 }}
                                        exit={{ opacity: 0, x: 20 }}
                                        key={file.id}
                                        className="border-b transition-colors hover:bg-muted/50 group flex flex-wrap md:table-row items-center"
                                    >
                                        {file.status !== 'completed' ? (
                                            <>
                                                <TableCell className="w-[50px] py-3 order-1 md:order-none md:w-[50px]">
                                                    <div className="h-12 w-12 flex items-center justify-center">
                                                        <Loader2 className="h-6 w-6 text-muted-foreground animate-spin" />
                                                    </div>
                                                </TableCell>
                                                <TableCell className="py-3 flex-1 order-2 md:order-none min-w-0 md:w-auto">
                                                    <div className="flex flex-col gap-1">
                                                        <div className="font-medium truncate">
                                                            {file.name}
                                                        </div>
                                                        <div className="text-sm text-muted-foreground">
                                                            Hochgeladen am {file.date}
                                                        </div>
                                                        <div className="flex items-center gap-2">
                                                            <Badge
                                                                variant="default"
                                                                className="gap-1 text-xs font-normal"
                                                                style={{
                                                                    backgroundColor: 'var(--chart-4)',
                                                                    color: 'var(--chart-4-foreground)',
                                                                    borderColor: 'var(--chart-4)'
                                                                }}
                                                            >
                                                                <Loader2 className="h-3 w-3 animate-spin mr-1" />
                                                                {file.phase === 'extracting' && 'Extrahiere Daten'}
                                                                {file.phase === 'categorizing' && 'Kategorisiere'}
                                                                {file.phase === 'validating' && 'Validiere'}
                                                                {file.phase === 'completed' && 'Exportbereit'}
                                                            </Badge>
                                                        </div>
                                                    </div>
                                                </TableCell>
                                                <TableCell className="text-right order-3 md:order-none md:min-w-[300px] whitespace-nowrap md:w-auto">
                                                </TableCell>
                                            </>
                                        ) : (
                                            <>
                                                <TableCell className="w-[50px] py-3 order-1 md:order-none md:w-[50px]">
                                                    <Button variant="ghost" size="icon" className="h-12 w-12">
                                                        <Eye className="h-6 w-6 text-muted-foreground" />
                                                    </Button>
                                                </TableCell>
                                                <TableCell className="py-3 flex-1 order-2 md:order-none min-w-0 md:w-auto pr-2 md:pr-4">
                                                    <div className="flex flex-col gap-1">
                                                        <div className="font-medium truncate">
                                                            {file.accounts?.[0].name}
                                                        </div>
                                                        <div className="text-sm text-muted-foreground">
                                                            RE-Nr: {file.invoiceNumber}
                                                        </div>
                                                        <div className="flex flex-wrap items-center gap-2">
                                                            <Badge
                                                                variant="default"
                                                                className="gap-1 text-xs font-normal px-2 py-0.5"
                                                                style={{
                                                                    backgroundColor: 'var(--chart-1)',
                                                                    color: 'var(--chart-1-foreground)',
                                                                    borderColor: 'var(--chart-1)'
                                                                }}
                                                            >
                                                                <Check className="h-3 w-3 mr-1" />
                                                                Gebucht
                                                            </Badge>
                                                        </div>
                                                    </div>
                                                </TableCell>
                                                <TableCell className="text-right order-3 md:order-none md:min-w-[300px] whitespace-nowrap md:w-auto flex-shrink-0 ml-auto md:ml-0">
                                                    <div className="flex flex-col items-end gap-1">
                                                        <div className="font-medium">
                                                            {file.amount} {file.currency === 'EUR' ? '€' : '$'}
                                                        </div>
                                                        {file.currency !== 'EUR' && file.convertedAmount && (
                                                            <div className="text-xs text-muted-foreground mt-0.5">
                                                                {file.convertedAmount} €
                                                            </div>
                                                        )}
                                                    </div>
                                                </TableCell>
                                            </>
                                        )}
                                    </motion.tr>
                                ))}
                            </AnimatePresence>
                        </TableBody>
                    </Table>
                </div>
                {/* Desktop: Scrollable */}
                <ScrollArea className="h-full hidden md:block">
                    <Table className="table-fixed w-full">
                        <TableBody>
                            <AnimatePresence mode='popLayout'>
                                {files.map((file) => (
                                    <motion.tr
                                        layout
                                        initial={{ opacity: 0, x: -20 }}
                                        animate={{ opacity: 1, x: 0 }}
                                        exit={{ opacity: 0, x: 20 }}
                                        key={file.id}
                                        className="border-b transition-colors hover:bg-muted/50 group flex flex-wrap md:table-row items-center"
                                    >
                                        {/* Render different content based on status, but in same row structure */}
                                        {file.status !== 'completed' ? (
                                            // Processing State
                                            <>
                                                <TableCell className="w-[50px] py-3 order-1 md:order-none md:w-[50px]">
                                                    <div className="h-12 w-12 flex items-center justify-center">
                                                        <Loader2 className="h-6 w-6 text-muted-foreground animate-spin" />
                                                    </div>
                                                </TableCell>
                                                <TableCell className="py-3 flex-1 order-2 md:order-none min-w-0 md:w-auto">
                                                    <div className="flex flex-col gap-1">
                                                        <div className="font-medium truncate">
                                                            {file.name}
                                                        </div>
                                                        <div className="text-sm text-muted-foreground">
                                                            Hochgeladen am {file.date}
                                                        </div>
                                                        <div className="flex items-center gap-2">
                                                            <Badge
                                                                variant="default"
                                                                className="gap-1 text-xs font-normal"
                                                                style={{
                                                                    backgroundColor: 'var(--chart-4)',
                                                                    color: 'var(--chart-4-foreground)',
                                                                    borderColor: 'var(--chart-4)'
                                                                }}
                                                            >
                                                                <Loader2 className="h-3 w-3 animate-spin mr-1" />
                                                                {file.phase === 'extracting' && 'Extrahiere Daten'}
                                                                {file.phase === 'categorizing' && 'Kategorisiere'}
                                                                {file.phase === 'validating' && 'Validiere'}
                                                                {file.phase === 'completed' && 'Exportbereit'}
                                                            </Badge>
                                                        </div>
                                                    </div>
                                                </TableCell>
                                                <TableCell className="py-3 w-full order-4 md:order-none md:w-auto pl-[66px] md:pl-4">
                                                    <div className="flex flex-col gap-2">
                                                        <div className="text-sm text-muted-foreground">
                                                            {file.message}
                                                        </div>
                                                        <div className="w-full bg-secondary rounded-full h-2 overflow-hidden">
                                                            <motion.div
                                                                className="h-full bg-primary"
                                                                initial={{ width: 0 }}
                                                                animate={{ width: `${file.progress}%` }}
                                                                transition={{ ease: "linear", duration: 0.1 }}
                                                            />
                                                        </div>
                                                        <div className="text-xs text-muted-foreground">
                                                            {Math.round(file.progress)}%
                                                        </div>
                                                    </div>
                                                </TableCell>
                                                <TableCell className="text-right order-3 md:order-none md:min-w-[300px] whitespace-nowrap md:w-auto">
                                                    {/* Empty for processing */}
                                                </TableCell>
                                                <TableCell className="w-[50px] order-5 md:order-none md:w-[50px]">
                                                    {/* Empty for processing */}
                                                </TableCell>
                                            </>
                                        ) : (
                                            // Completed State
                                            <>
                                                <TableCell className="w-[50px] py-3 order-1 md:order-none md:w-[50px]">
                                                    <Button variant="ghost" size="icon" className="h-12 w-12">
                                                        <Eye className="h-6 w-6 text-muted-foreground" />
                                                    </Button>
                                                </TableCell>
                                                <TableCell className="py-3 flex-1 order-2 md:order-none min-w-0 md:w-auto pr-2 md:pr-4">
                                                    <div className="flex flex-col gap-1">
                                                        <div className="font-medium truncate">
                                                            {file.accounts?.[0].name}
                                                        </div>
                                                        <div className="text-sm text-muted-foreground">
                                                            RE-Nr: {file.invoiceNumber}
                                                        </div>
                                                        <div className="flex flex-wrap items-center gap-2">
                                                            <span className="text-sm text-muted-foreground whitespace-nowrap flex flex-wrap gap-x-2">
                                                                <span>Beleg: {file.date}</span>
                                                                <span>Buchung: {file.date}</span>
                                                            </span>
                                                            <Badge
                                                                variant="default"
                                                                className="gap-1 text-xs font-normal px-2 py-0.5"
                                                                style={{
                                                                    backgroundColor: 'var(--chart-1)',
                                                                    color: 'var(--chart-1-foreground)',
                                                                    borderColor: 'var(--chart-1)'
                                                                }}
                                                            >
                                                                <Check className="h-3 w-3 mr-1" />
                                                                Gebucht
                                                            </Badge>
                                                        </div>
                                                    </div>
                                                </TableCell>
                                                <TableCell className="py-3 w-full order-4 md:order-none md:w-auto pl-[66px] md:pl-4">
                                                    <div className="flex flex-col gap-1 text-sm">
                                                        <div className="font-medium">
                                                            Debitor: {file.accounts?.[0].debit} - {file.accounts?.[0].name}
                                                        </div>
                                                        <div className="text-muted-foreground">
                                                            Kreditor: {file.accounts?.[0].credit} - Bank
                                                        </div>
                                                    </div>
                                                </TableCell>
                                                <TableCell className="text-right order-3 md:order-none md:min-w-[300px] whitespace-nowrap md:w-auto flex-shrink-0 ml-auto md:ml-0">
                                                    <div className="flex flex-col items-end gap-1">
                                                        <div className="font-medium">
                                                            {file.amount} {file.currency === 'EUR' ? '€' : '$'}
                                                        </div>
                                                        {file.currency !== 'EUR' && file.convertedAmount && (
                                                            <div className="text-xs text-muted-foreground mt-0.5">
                                                                {file.convertedAmount} €
                                                            </div>
                                                        )}
                                                        <span className="text-sm text-muted-foreground whitespace-nowrap">
                                                            {file.tax}
                                                        </span>
                                                    </div>
                                                </TableCell>
                                                <TableCell className="w-[50px] order-5 md:order-none md:w-[50px]">
                                                    <Button
                                                        variant="ghost"
                                                        size="icon"
                                                        className="h-8 w-8 opacity-0 group-hover:opacity-100"
                                                    >
                                                        <MoreVertical className="h-4 w-4" />
                                                    </Button>
                                                </TableCell>
                                            </>
                                        )}
                                    </motion.tr>
                                ))}
                            </AnimatePresence>
                        </TableBody>
                    </Table>
                </ScrollArea>
            </div>

            {/* Footer */}
            <div className="flex items-center justify-between px-4 py-4 border-t bg-background rounded-b-md">
                <div className="hidden items-center space-x-2 md:flex">
                    <p className="text-sm text-muted-foreground">
                        Zeige <span className="font-medium">1</span> bis{" "}
                        <span className="font-medium">{Math.min(5, files.length)}</span> von{" "}
                        <span className="font-medium">{files.length}</span> Einträgen
                    </p>
                </div>
                <div className="flex items-center space-x-6">
                    <div className="flex items-center space-x-2 opacity-50 pointer-events-none">
                        <p className="text-sm text-muted-foreground">Zeilen pro Seite</p>
                        <Select defaultValue="25" disabled>
                            <SelectTrigger className="h-8 w-[70px]">
                                <SelectValue placeholder="25" />
                            </SelectTrigger>
                            <SelectContent>
                                <SelectItem value="25">25</SelectItem>
                            </SelectContent>
                        </Select>
                    </div>
                    <div className="flex items-center space-x-2 opacity-50 pointer-events-none">
                        <Button variant="outline" size="icon" className="h-8 w-8" disabled>
                            <ChevronLeft className="h-4 w-4" />
                        </Button>
                        <Button variant="default" size="icon" className="h-8 w-8">
                            1
                        </Button>
                        <Button variant="outline" size="icon" className="h-8 w-8" disabled>
                            <ChevronRight className="h-4 w-4" />
                        </Button>
                    </div>
                </div>
            </div>
        </div>
    );
}
